import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

export function CliAuth() {
  useEffect(() => {
    document.title = "vibe-check ⏵CLI Auth";
  }, []);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, login } = useAuth();

  const [code, setCode] = useState(searchParams.get("code") || "");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  // Auto-fill code from URL
  useEffect(() => {
    const urlCode = searchParams.get("code");
    if (urlCode) {
      setCode(urlCode);
    }
  }, [searchParams]);

  const handleApprove = async () => {
    if (!code.trim()) {
      setError("Please enter the code shown in your terminal");
      return;
    }

    setStatus("loading");
    setError(null);

    try {
      await api.approveCliAuth(code);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Failed to approve");
    }
  };

  // Format code as user types (XXXX-XXXX)
  const handleCodeChange = (value: string) => {
    // Remove non-alphanumeric characters
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    // Add dash after 4 characters
    if (clean.length > 4) {
      setCode(clean.slice(0, 4) + "-" + clean.slice(4, 8));
    } else {
      setCode(clean);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <h1 className="text-2xl font-bold text-white mb-4">
          Authorize CLI Device
        </h1>
        <p className="text-gray-400 mb-6">
          Please log in to authorize your CLI device.
        </p>
        <button
          onClick={() => login(location.pathname + location.search)}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Login with GitHub
        </button>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-4">
          Device Authorized!
        </h1>
        <p className="text-gray-400 mb-6">
          Your CLI is now connected. You can now view and analyze your messages.
        </p>
        <button
          onClick={() => navigate("/messages")}
          className="text-purple-400 hover:text-purple-300"
        >
          Go to Messages
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-12">
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        Authorize CLI Device
      </h1>
      <p className="text-gray-400 text-center mb-8">
        Enter the code shown in your terminal to authorize the vibe-check CLI.
      </p>

      <div className="bg-gray-800 rounded-lg p-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Device Code
        </label>
        <input
          type="text"
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder="XXXX-XXXX"
          maxLength={9}
          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-2xl tracking-widest font-mono focus:outline-none focus:border-purple-500"
          autoFocus
        />

        {error && (
          <p className="mt-3 text-red-400 text-sm text-center">{error}</p>
        )}

        <button
          onClick={handleApprove}
          disabled={status === "loading" || code.length < 9}
          className="w-full mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === "loading" ? (
            <span className="flex items-center justify-center">
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></span>
              Authorizing...
            </span>
          ) : (
            "Authorize Device"
          )}
        </button>
      </div>

      <p className="mt-6 text-sm text-gray-500 text-center">
        This will create an API key for the CLI to access your vibe-check data.
      </p>
    </div>
  );
}
